import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { URL as BACKEND_URL, fetchNewToken } from '../../gql/client'
import { STORAGE_NAMES } from '../../helpers/consts'
import { useTelephony } from '../../hooks/useTelephony'

interface LoggedInAgent {
  user: string
  campaign_id: string
  conf_exten: string
  status: string
  lead_id?: string
  callerid?: string
  calls_today: string
  policytools_calls_today?: string
  full_name: string
  user_group: string
  user_level?: string
}

interface DashboardResponse {
  provider: string
  agents: LoggedInAgent[]
  calls_in_queue_count: number
  raw_response: string
}

interface AgentMetric {
  agent_user: string
  total_calls: number
  dispositioned: number
  dispo_rate: number
  avg_talk_time_seconds: number
  calls_per_hour: number
}

interface CampaignMetric {
  campaign_id: string
  total_calls: number
  dispositioned: number
  dispo_rate: number
  avg_talk_time_seconds: number
  calls_per_hour: number
}

interface DispositionBreakdown {
  disposition: string
  count: number
}

interface MetricsResponse {
  since_hours: number
  total_calls: number
  dispositioned: number
  dispo_rate: number
  avg_talk_time_seconds: number
  calls_per_hour: number
  per_agent: AgentMetric[]
  per_campaign: CampaignMetric[]
  dispositions: DispositionBreakdown[]
}

interface SupervisorUser {
  agent_user: string
  full_name: string
  user_group: string
  live: boolean
}

interface SupervisorUsersResponse {
  since_days: number
  users: SupervisorUser[]
}

interface SupervisorLogoutResponse {
  provider: string
  agent_user: string
  target_agent_user?: string
  supervisor_user?: string
  active_session: boolean
  status: string
  status_before_disconnect: string
  campaign_id: string
  session_id: string
  phone_number: string
  raw_response: string
  hangup_raw_response: string
}

type SupervisorBulkAction = 'logout' | 'pause' | 'resume'
type SupervisorMonitorStage = 'MONITOR' | 'BARGE' | 'WHISPER'

interface PendingSupervisorAction {
  action: SupervisorBulkAction
  agentUsers: string[]
}

interface SupervisorMonitorRequest {
  agentUser: string
  stage: SupervisorMonitorStage
  requestId: number
}

interface TelephonySupervisorLocationState {
  pendingSupervisorMonitor?: SupervisorMonitorRequest
}

interface IntegrationsLocationState {
  supervisorMonitorRequest?: SupervisorMonitorRequest
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authGet = async <T,>(url: string): Promise<T> => {
  try {
    const res = await axios.get<T>(url, { headers: authHeaders() })
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        const res = await axios.get<T>(url, { headers: { Authorization: `Bearer ${newToken}` } })
        return res.data
      }
    }
    throw err
  }
}

const authPost = async <T,>(url: string, body: any): Promise<T> => {
  try {
    const res = await axios.post<T>(url, body, { headers: authHeaders() })
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        const res = await axios.post<T>(url, body, {
          headers: { Authorization: `Bearer ${newToken}` },
        })
        return res.data
      }
    }
    throw err
  }
}

const STATUS_VARIANT: Record<string, string> = {
  READY: 'success',
  INCALL: 'primary',
  QUEUE: 'info',
  PAUSED: 'warning',
  CLOSER: 'success',
  DEAD: 'danger',
  DISPO: 'secondary',
}

const STATUS_BADGE_STYLE: Record<string, CSSProperties> = {
  READY: { backgroundColor: '#DCFCE7', color: '#166534' },
  INCALL: { backgroundColor: '#DBEAFE', color: '#1D4ED8' },
  QUEUE: { backgroundColor: '#E0F2FE', color: '#0369A1' },
  PAUSED: { backgroundColor: '#FEF3C7', color: '#B45309' },
  CLOSER: { backgroundColor: '#DCFCE7', color: '#166534' },
  DEAD: { backgroundColor: '#FEE2E2', color: '#DC2626' },
  DISPO: { backgroundColor: '#E5E7EB', color: '#4B5563' },
}

const normalizeStatus = (status: string) => String(status || '').trim().toUpperCase()
const LIVE_WAITING_STATUSES = new Set(['READY', 'CLOSER'])

const formatSeconds = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (!mins) return `${secs}s`
  return `${mins}m ${secs}s`
}

const formatPercent = (ratio: number) => `${(ratio * 100).toFixed(1)}%`

const REFRESH_INTERVAL_MS = 4000

export const TelephonySupervisorPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const telephony = useTelephony()
  const hasAccess = telephony.config.vicidial_supervisor_access
  const resolved = telephony.resolved
  const handledMonitorRequestRef = useRef(0)

  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null)
  const [dashboardError, setDashboardError] = useState('')
  const [loadingDashboard, setLoadingDashboard] = useState(false)

  const [metrics, setMetrics] = useState<MetricsResponse | null>(null)
  const [metricsError, setMetricsError] = useState('')
  const [loadingMetrics, setLoadingMetrics] = useState(false)

  const [supervisorUsers, setSupervisorUsers] = useState<SupervisorUser[]>([])

  const [sinceHours, setSinceHours] = useState<number>(24)
  const [campaignFilter, setCampaignFilter] = useState('')
  const [agentFilter, setAgentFilter] = useState('')
  const [liveGroupFilter, setLiveGroupFilter] = useState('')
  const [liveCampaignFilter, setLiveCampaignFilter] = useState('')

  const [monitoringAgent, setMonitoringAgent] = useState<string>('')
  const [selectedAgentUsers, setSelectedAgentUsers] = useState<string[]>([])
  const [runningSupervisorAction, setRunningSupervisorAction] = useState(false)
  const [pendingSupervisorAction, setPendingSupervisorAction] =
    useState<PendingSupervisorAction | null>(null)
  const [activeMonitor, setActiveMonitor] = useState<{
    targetAgentUser: string
    targetAgentName: string
    stage: SupervisorMonitorStage
  } | null>(null)

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoadingDashboard(true)
    try {
      const data = await authGet<DashboardResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/dashboard?provider=VICIDIAL`
      )
      setDashboard(data)
      setDashboardError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load supervisor dashboard'
      setDashboardError(msg)
    } finally {
      if (!silent) setLoadingDashboard(false)
    }
  }, [])

  const loadSupervisorUsers = useCallback(async () => {
    try {
      const data = await authGet<SupervisorUsersResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/users?since_days=90&provider=VICIDIAL`
      )
      setSupervisorUsers(data?.users || [])
    } catch {
      // Silently ignore — fallback to live agents in dropdown.
    }
  }, [])

  const loadMetrics = useCallback(async () => {
    setLoadingMetrics(true)
    try {
      const params = new URLSearchParams()
      params.set('since_hours', String(sinceHours || 24))
      if (campaignFilter) params.set('campaign_id', campaignFilter)
      if (agentFilter) params.set('agent_user', agentFilter)
      const data = await authGet<MetricsResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/metrics?${params.toString()}`
      )
      setMetrics(data)
      setMetricsError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load metrics'
      setMetricsError(msg)
    } finally {
      setLoadingMetrics(false)
    }
  }, [sinceHours, campaignFilter, agentFilter])

  useEffect(() => {
    if (!hasAccess) return
    loadDashboard(false)
    loadMetrics()
    loadSupervisorUsers()
  }, [hasAccess, loadDashboard, loadMetrics, loadSupervisorUsers])

  useEffect(() => {
    if (!hasAccess) return
    const id = setInterval(() => loadDashboard(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [hasAccess, loadDashboard])

  const startMonitor = useCallback(
    async (agentUser: string, stage: SupervisorMonitorStage) => {
      if (!agentUser) return
      if (activeMonitor) {
        toast.info('Stop the active monitor session before starting a new one.')
        return
      }
      setMonitoringAgent(agentUser)
      try {
        await authPost(`${BACKEND_URL}/api/telephony/supervisor/monitor`, {
          agent_user: agentUser,
          stage,
          provider: 'VICIDIAL',
        })
        const targetName =
          dashboard?.agents?.find((a) => a.user === agentUser)?.full_name ||
          supervisorUsers.find((u) => u.agent_user === agentUser)?.full_name ||
          agentUser
        setActiveMonitor({
          targetAgentUser: agentUser,
          targetAgentName: targetName,
          stage,
        })
        toast.success(`${stage} started for ${agentUser}. Answer in PolicyDial when it rings.`)
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || `${stage} failed`
        toast.error(msg)
      } finally {
        setMonitoringAgent('')
      }
    },
    [activeMonitor, dashboard, supervisorUsers]
  )

  useEffect(() => {
    const pendingRequest = (location.state as TelephonySupervisorLocationState | null)
      ?.pendingSupervisorMonitor
    if (!hasAccess || !pendingRequest?.requestId) return
    if (handledMonitorRequestRef.current === pendingRequest.requestId) return

    handledMonitorRequestRef.current = pendingRequest.requestId
    navigate('/telephony-supervisor', { replace: true })
    void startMonitor(pendingRequest.agentUser, pendingRequest.stage)
  }, [hasAccess, location.state, navigate, startMonitor])

  const handleMonitor = useCallback(
    (agentUser: string, stage: SupervisorMonitorStage) => {
      if (!agentUser) return
      if (activeMonitor) {
        toast.info('Stop the active monitor session before starting a new one.')
        return
      }

      const request: SupervisorMonitorRequest = {
        agentUser,
        stage,
        requestId: Date.now(),
      }
      navigate('/integrations', {
        state: {
          supervisorMonitorRequest: request,
        } as IntegrationsLocationState,
      })
    },
    [activeMonitor, navigate]
  )

  const handleStopMonitor = useCallback(() => {
    if (!activeMonitor) return
    setActiveMonitor(null)
    toast.info('Monitor cleared. Hang up in PolicyDial if the call is still active.')
  }, [activeMonitor])

  const requestSupervisorAction = useCallback((action: SupervisorBulkAction, agentUsers: string[]) => {
    const uniqueAgentUsers = Array.from(
      new Set(agentUsers.map((agentUser) => String(agentUser || '').trim()).filter(Boolean))
    )
    if (!uniqueAgentUsers.length) return
    setPendingSupervisorAction({ action, agentUsers: uniqueAgentUsers })
  }, [])

  const confirmSupervisorAction = useCallback(async () => {
    const pendingAction = pendingSupervisorAction
    if (!pendingAction?.agentUsers.length) return

    setPendingSupervisorAction(null)
    setRunningSupervisorAction(true)

    const action = pendingAction.action
    let successCount = 0
    let warningCount = 0
    const failures: string[] = []

    try {
      for (const agentUser of pendingAction.agentUsers) {
        try {
          if (action === 'logout') {
            const data = await authPost<SupervisorLogoutResponse>(
              `${BACKEND_URL}/api/telephony/supervisor/logout`,
              {
                agent_user: agentUser,
                provider: 'VICIDIAL',
                force_hangup: true,
              }
            )
            if (data.active_session) {
              warningCount += 1
            } else {
              successCount += 1
            }
            continue
          }

          await authPost(`${BACKEND_URL}/api/telephony/supervisor/pause`, {
            agent_user: agentUser,
            provider: 'VICIDIAL',
            paused: action === 'pause',
          })
          successCount += 1
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.message || `${action} failed`
          failures.push(`${agentUser}: ${msg}`)
        }
      }

      if (action === 'logout') {
        if (successCount > 0) {
          toast.success(
            `${successCount} agent${successCount === 1 ? '' : 's'} logged out from PolicyDial.`
          )
        }
        if (warningCount > 0) {
          toast.warning(
            `${warningCount} agent${warningCount === 1 ? '' : 's'} still appear active after logout.`
          )
        }
      } else if (successCount > 0) {
        toast.success(
          `${successCount} agent${successCount === 1 ? '' : 's'} ${
            action === 'pause' ? 'paused' : 'resumed'
          } in PolicyDial.`
        )
      }

      if (failures.length > 0) {
        toast.error(
          failures.length === 1
            ? failures[0]
            : `${failures.length} agents failed during ${action}. First error: ${failures[0]}`
        )
      }
    } finally {
      setRunningSupervisorAction(false)
      setSelectedAgentUsers([])
      await loadDashboard(true)
    }
  }, [loadDashboard, pendingSupervisorAction])

  const agents = useMemo(() => dashboard?.agents ?? [], [dashboard?.agents])
  const agentMap = useMemo(
    () => new Map(agents.map((agent) => [String(agent.user || '').trim(), agent])),
    [agents]
  )
  const liveUserGroups = useMemo(
    () =>
      Array.from(
        new Set(
          agents.map((agent) => String(agent.user_group || '').trim()).filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [agents]
  )
  const liveCampaigns = useMemo(
    () =>
      Array.from(
        new Set(
          agents.map((agent) => String(agent.campaign_id || '').trim()).filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [agents]
  )
  const agentFilterOptions = useMemo(() => {
    const map = new Map<string, { userId: string; label: string; live: boolean }>()
    supervisorUsers.forEach((entry) => {
      const userId = String(entry.agent_user || '').trim()
      if (!userId) return
      const fullName = String(entry.full_name || '').trim()
      const group = String(entry.user_group || '').trim()
      const liveTag = entry.live ? '● live' : ''
      const label = [userId, fullName, group ? `(${group})` : '', liveTag]
        .filter(Boolean)
        .join(' — ')
      map.set(userId, { userId, label, live: !!entry.live })
    })
    agents.forEach((agent) => {
      const userId = String(agent.user || '').trim()
      if (!userId || map.has(userId)) return
      const fullName = String(agent.full_name || '').trim()
      const group = String(agent.user_group || '').trim()
      const label = [userId, fullName, group ? `(${group})` : '', '● live']
        .filter(Boolean)
        .join(' — ')
      map.set(userId, { userId, label, live: true })
    })
    return Array.from(map.values()).sort((left, right) => {
      if (left.live !== right.live) return left.live ? -1 : 1
      return left.userId.localeCompare(right.userId)
    })
  }, [supervisorUsers, agents])
  const liveAgents = useMemo(
    () =>
      agents.filter((agent) => {
        const groupMatches =
          !liveGroupFilter || String(agent.user_group || '').trim() === liveGroupFilter
        const campaignMatches =
          !liveCampaignFilter || String(agent.campaign_id || '').trim() === liveCampaignFilter
        return groupMatches && campaignMatches
      }),
    [agents, liveCampaignFilter, liveGroupFilter]
  )
  const visibleAgentUsers = useMemo(
    () => Array.from(new Set(liveAgents.map((agent) => String(agent.user || '').trim()).filter(Boolean))),
    [liveAgents]
  )
  const allVisibleSelected = useMemo(
    () =>
      visibleAgentUsers.length > 0 &&
      visibleAgentUsers.every((agentUser) => selectedAgentUsers.includes(agentUser)),
    [selectedAgentUsers, visibleAgentUsers]
  )
  const totalInCall = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'INCALL').length,
    [liveAgents]
  )
  const totalWaiting = useMemo(
    () => liveAgents.filter((a) => LIVE_WAITING_STATUSES.has(normalizeStatus(a.status))).length,
    [liveAgents]
  )
  const totalPaused = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'PAUSED').length,
    [liveAgents]
  )
  const totalInDispo = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'DISPO').length,
    [liveAgents]
  )
  const totalDeadCalls = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'DEAD').length,
    [liveAgents]
  )
  const summaryCards = useMemo(
    () => [
      {
        label: 'Agents logged in',
        value: liveAgents.length,
        valueClassName: '',
        icon: 'bi-people-fill',
        iconColor: '#0d6efd',
        iconBackground: 'rgba(13, 110, 253, 0.12)',
        helper: `${liveAgents.length} active PolicyDial sessions`,
      },
      {
        label: 'Agents in calls',
        value: totalInCall,
        valueClassName: 'text-primary',
        icon: 'bi-telephone-fill',
        iconColor: '#0d6efd',
        iconBackground: 'rgba(13, 110, 253, 0.12)',
        helper: 'Live call sessions in progress',
      },
      {
        label: 'Agents waiting',
        value: totalWaiting,
        valueClassName: 'text-success',
        icon: 'bi-check-circle-fill',
        iconColor: '#198754',
        iconBackground: 'rgba(25, 135, 84, 0.14)',
        helper: 'READY and CLOSER states',
      },
      {
        label: 'Paused agents',
        value: totalPaused,
        valueClassName: 'text-warning',
        icon: 'bi-pause-circle-fill',
        iconColor: '#f59f00',
        iconBackground: 'rgba(245, 159, 0, 0.14)',
        helper: 'Agents temporarily unavailable',
      },
      {
        label: 'Agents in dispo',
        value: totalInDispo,
        valueClassName: 'text-secondary',
        icon: 'bi-journal-check',
        iconColor: '#6c757d',
        iconBackground: 'rgba(108, 117, 125, 0.14)',
        helper: 'Wrap-up / disposition work',
      },
      {
        label: 'Dead calls',
        value: totalDeadCalls,
        valueClassName: 'text-danger',
        icon: 'bi-telephone-x-fill',
        iconColor: '#dc3545',
        iconBackground: 'rgba(220, 53, 69, 0.12)',
        helper: 'Sessions marked DEAD in PolicyDial',
      },
    ],
    [liveAgents.length, totalDeadCalls, totalInCall, totalInDispo, totalPaused, totalWaiting]
  )

  useEffect(() => {
    if (!selectedAgentUsers.length) return
    const visibleSet = new Set(visibleAgentUsers)
    setSelectedAgentUsers((current) => current.filter((agentUser) => visibleSet.has(agentUser)))
  }, [selectedAgentUsers.length, visibleAgentUsers])

  const toggleSelectVisibleAgents = useCallback(() => {
    if (runningSupervisorAction) return
    setSelectedAgentUsers((current) => {
      if (allVisibleSelected) {
        const visibleSet = new Set(visibleAgentUsers)
        return current.filter((agentUser) => !visibleSet.has(agentUser))
      }
      return Array.from(new Set([...current, ...visibleAgentUsers]))
    })
  }, [allVisibleSelected, runningSupervisorAction, visibleAgentUsers])

  const toggleSelectAgent = useCallback((agentUser: string) => {
    if (!agentUser || runningSupervisorAction) return
    setSelectedAgentUsers((current) =>
      current.includes(agentUser)
        ? current.filter((existingAgentUser) => existingAgentUser !== agentUser)
        : [...current, agentUser]
    )
  }, [runningSupervisorAction])

  const pendingActionType = pendingSupervisorAction?.action || 'logout'
  const pendingActionAgents = pendingSupervisorAction?.agentUsers || []
  const pendingLiveRiskCount = pendingActionAgents.filter((agentUser) => {
    const status = normalizeStatus(agentMap.get(agentUser)?.status || '')
    return status === 'INCALL' || status === 'QUEUE' || status === 'DISPO'
  }).length
  const pendingActionTitle =
    pendingActionType === 'logout'
      ? 'Confirm Force Logout'
      : pendingActionType === 'pause'
        ? 'Confirm Bulk Pause'
        : 'Confirm Bulk Resume'
  const pendingActionPrimaryLabel =
    pendingActionType === 'logout'
      ? 'Force Logout'
      : pendingActionType === 'pause'
        ? 'Pause Selected'
        : 'Resume Selected'
  const pendingActionPrimaryVariant =
    pendingActionType === 'logout'
      ? 'danger'
      : pendingActionType === 'pause'
        ? 'warning'
        : 'success'
  const pendingActionDescription =
    pendingActionType === 'logout'
      ? 'This will disconnect the selected PolicyDial sessions. Any active calls on those sessions may be dropped.'
      : pendingActionType === 'pause'
        ? 'This will set the selected PolicyDial agents to a paused state.'
        : 'This will attempt to return the selected PolicyDial agents to an active state.'
  const pendingActionNoticeStyle: CSSProperties =
    pendingLiveRiskCount > 0 || pendingActionType === 'logout'
      ? {
          backgroundColor: '#FFF7E6',
          border: '1px solid #FCD34D',
          color: '#92400E',
        }
      : {
          backgroundColor: '#F8FAFC',
          border: '1px solid #CBD5E1',
          color: '#334155',
        }

  if (resolved && !hasAccess) {
    return (
      <div className='container-fluid py-4'>
        <Alert variant='warning' className='d-flex align-items-center gap-3'>
          <i className='bi bi-shield-lock fs-3' />
          <div>
            <div className='fw-semibold'>Access restricted</div>
            <div className='text-muted fs-7'>
              Your account does not have access to the PolicyDial supervisor dashboard. Ask an
              administrator to enable it from Users → PolicyDial Settings.
            </div>
          </div>
        </Alert>
      </div>
    )
  }

  return (
    <div className='container-fluid py-4'>
      <div className='d-flex align-items-center mb-4 gap-2'>
        <Button
          variant='light'
          size='sm'
          onClick={() => navigate('/integrations')}
          title='Back to integrations'
        >
          <i className='bi bi-arrow-left me-1' />
          Back
        </Button>
        <h2 className='mb-0 mx-2'>Telephony Supervisor</h2>
        <Button
          variant='light'
          size='sm'
          onClick={() => {
            loadDashboard(false)
            loadMetrics()
          }}
          disabled={loadingDashboard || loadingMetrics}
        >
          <i className='bi bi-arrow-clockwise me-1' />
          Refresh
        </Button>
        <Button
          variant='light'
          size='sm'
          className='ms-auto'
          onClick={() => navigate('/integrations')}
          title='Close'
          aria-label='Close'
        >
          <i className='bi bi-x-lg' />
        </Button>
      </div>

      {activeMonitor && (
        <Alert
          variant='info'
          className='d-flex align-items-center justify-content-between gap-3 flex-wrap mb-4'
        >
          <div>
            <div className='fw-semibold mb-1'>
              <i className='bi bi-headphones me-2' />
              {activeMonitor.stage} active on {activeMonitor.targetAgentName}
              {activeMonitor.targetAgentName !== activeMonitor.targetAgentUser
                ? ` (${activeMonitor.targetAgentUser})`
                : ''}
            </div>
            <div className='small text-muted'>
              Answer the incoming call in the PolicyDial drawer. Hang up in PolicyDial when you
              are done — then click Done here to clear this banner.
            </div>
          </div>
          <Button variant='outline-secondary' size='sm' onClick={handleStopMonitor}>
            <i className='bi bi-check2 me-1' />
            Done
          </Button>
        </Alert>
      )}

      <Row className='g-3 mb-4'>
        {summaryCards.map((card) => (
          <Col md={6} xl={4} xxl={2} key={card.label}>
            <Card className='shadow-sm border-0 h-100'>
              <Card.Body className='d-flex align-items-start justify-content-between gap-3'>
                <div>
                  <div className='text-muted small text-uppercase fw-semibold'>{card.label}</div>
                  <div className={`fs-2 fw-bold ${card.valueClassName}`.trim()}>{card.value}</div>
                  <div>{card.helper}</div>
                </div>
                <div
                  className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                  style={{
                    width: 56,
                    height: 56,
                    backgroundColor: card.iconBackground,
                    color: card.iconColor,
                  }}
                >
                  <i className={`${card.icon} fs-2`} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className='shadow-sm mb-4'>
        <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div>
            <div className='fw-semibold'>Live PolicyDial</div>
            <div className='text-muted small'>
              Session counts and agent statuses come directly from PolicyDial.
            </div>
          </div>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <Badge bg='light' text='dark' className='border'>
              Global queue calls: {dashboard?.calls_in_queue_count ?? 0}
            </Badge>
            <Form.Select
              size='sm'
              style={{ width: 180 }}
              value={liveGroupFilter}
              onChange={(e) => setLiveGroupFilter(e.target.value)}
            >
              <option value=''>All user groups</option>
              {liveUserGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size='sm'
              style={{ width: 160 }}
              value={liveCampaignFilter}
              onChange={(e) => setLiveCampaignFilter(e.target.value)}
            >
              <option value=''>All campaigns</option>
              {liveCampaigns.map((campaign) => (
                <option key={campaign} value={campaign}>
                  {campaign}
                </option>
              ))}
            </Form.Select>
            <Button
              size='sm'
              variant='light'
              onClick={() => {
                setLiveGroupFilter('')
                setLiveCampaignFilter('')
              }}
              disabled={!liveGroupFilter && !liveCampaignFilter}
            >
              Reset
            </Button>
            {loadingDashboard && <Spinner animation='border' size='sm' />}
          </div>
        </Card.Header>
        <Card.Body className='p-0'>
          {selectedAgentUsers.length > 0 && (
            <div
              className='d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-3 border-bottom'
              style={{ backgroundColor: '#F8FAFC' }}
            >
              <div className='small fw-semibold text-dark'>
                {selectedAgentUsers.length} agent{selectedAgentUsers.length === 1 ? '' : 's'} selected
              </div>
              <ButtonGroup size='sm'>
                <Button
                  variant='outline-warning'
                  disabled={runningSupervisorAction}
                  onClick={() => requestSupervisorAction('pause', selectedAgentUsers)}
                >
                  <i className='bi bi-pause-circle me-1' />
                  Pause Selected
                </Button>
                <Button
                  variant='outline-success'
                  disabled={runningSupervisorAction}
                  onClick={() => requestSupervisorAction('resume', selectedAgentUsers)}
                >
                  <i className='bi bi-play-circle me-1' />
                  Resume Selected
                </Button>
                <Button
                  variant='outline-danger'
                  disabled={runningSupervisorAction}
                  onClick={() => requestSupervisorAction('logout', selectedAgentUsers)}
                >
                  <i className='bi bi-box-arrow-right me-1' />
                  Force Logout
                </Button>
              </ButtonGroup>
            </div>
          )}
          {dashboardError && (
            <Alert variant='danger' className='m-3'>
              {dashboardError}
            </Alert>
          )}
          <Table hover responsive className='mb-0 align-middle'>
            <thead className='table-light'>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={allVisibleSelected}
                    disabled={!visibleAgentUsers.length || runningSupervisorAction}
                    onChange={toggleSelectVisibleAgents}
                    aria-label='Select all visible agents'
                  />
                </th>
                <th>Agent</th>
                <th>Name</th>
                <th>Campaign</th>
                <th>Session</th>
                <th>Status</th>
                <th>Group</th>
                <th className='text-end'>Calls today (PolicyDial)</th>
                <th className='text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 && !loadingDashboard && (
                <tr>
                  <td colSpan={9} className='text-center text-muted py-4'>
                    No agents currently logged in
                  </td>
                </tr>
              )}
              {agents.length > 0 && liveAgents.length === 0 && !loadingDashboard && (
                <tr>
                  <td colSpan={9} className='text-center text-muted py-4'>
                    No live agents match the current filters
                  </td>
                </tr>
              )}
              {liveAgents.map((a) => {
                const status = normalizeStatus(a.status)
                const variant = STATUS_VARIANT[status] || 'light'
                const badgeStyle = STATUS_BADGE_STYLE[status] || { backgroundColor: '#F3F4F6', color: '#111827' }
                const rowBusy = monitoringAgent === a.user || runningSupervisorAction
                return (
                  <tr key={`${a.user}-${a.conf_exten}`}>
                    <td>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={selectedAgentUsers.includes(a.user)}
                        disabled={runningSupervisorAction}
                        onChange={() => toggleSelectAgent(a.user)}
                        aria-label={`Select ${a.user}`}
                      />
                    </td>
                    <td className='fw-semibold'>{a.user}</td>
                    <td>{a.full_name || '—'}</td>
                    <td>{a.campaign_id || '—'}</td>
                    <td>{a.conf_exten || '—'}</td>
                    <td>
                      <Badge bg={variant} style={badgeStyle} className='border-0'>
                        {status || '—'}
                      </Badge>
                    </td>
                    <td>{a.user_group || '—'}</td>
                    <td className='text-end'>{a.calls_today || 0}</td>
                    <td className='text-end'>
                      <ButtonGroup size='sm'>
                        <Button
                          variant='outline-info'
                          disabled={status !== 'INCALL' || rowBusy}
                          title='Silent monitor'
                          onClick={() => handleMonitor(a.user, 'MONITOR')}
                        >
                          <i className='bi bi-headphones' /> Monitor
                        </Button>
                        <Button
                          variant='outline-primary'
                          disabled={status !== 'INCALL' || rowBusy}
                          title='Whisper to agent'
                          onClick={() => handleMonitor(a.user, 'WHISPER')}
                        >
                          <i className='bi bi-megaphone' /> Whisper
                        </Button>
                        <Button
                          variant='outline-danger'
                          disabled={status !== 'INCALL' || rowBusy}
                          title='Barge (two-way)'
                          onClick={() => handleMonitor(a.user, 'BARGE')}
                        >
                          <i className='bi bi-telephone-plus' /> Barge
                        </Button>
                        <Button
                          variant='outline-secondary'
                          disabled={rowBusy}
                          title='Force logout from PolicyDial'
                          onClick={() => requestSupervisorAction('logout', [a.user])}
                        >
                          <i className='bi bi-box-arrow-right' />{' '}
                          {runningSupervisorAction ? 'Working...' : 'Logout'}
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className='shadow-sm'>
        <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div>
            <div className='fw-semibold'>PolicyTools Metrics</div>
            <div className='text-muted small'>
              Historical analytics based on local call records and webhook sync.
            </div>
          </div>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <Form.Select
              size='sm'
              style={{ width: 140 }}
              value={sinceHours}
              onChange={(e) => setSinceHours(Number(e.target.value))}
            >
              <option value={1}>Last 1 hour</option>
              <option value={4}>Last 4 hours</option>
              <option value={8}>Last 8 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
            </Form.Select>
            <Form.Select
              size='sm'
              style={{ width: 160 }}
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
            >
              <option value=''>All campaigns</option>
              {liveCampaigns.map((campaignId) => (
                <option key={campaignId} value={campaignId}>
                  {campaignId}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size='sm'
              style={{ width: 240 }}
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
            >
              <option value=''>All agents</option>
              {agentFilterOptions.map((option) => (
                <option key={option.userId} value={option.userId}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Button size='sm' onClick={loadMetrics} disabled={loadingMetrics}>
              {loadingMetrics ? <Spinner animation='border' size='sm' /> : 'Apply'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {metricsError && <Alert variant='danger'>{metricsError}</Alert>}

          <Row className='g-3 mb-4'>
            <Col md={3}>
              <div className='text-muted small'>Total calls</div>
              <div className='fs-3 fw-bold'>{metrics?.total_calls ?? 0}</div>
            </Col>
            <Col md={3}>
              <div className='text-muted small'>Dispo rate</div>
              <div className='fs-3 fw-bold'>{formatPercent(metrics?.dispo_rate ?? 0)}</div>
              <div className='text-muted small'>
                {metrics?.dispositioned ?? 0} / {metrics?.total_calls ?? 0}
              </div>
            </Col>
            <Col md={3}>
              <div className='text-muted small'>Avg talk time</div>
              <div className='fs-3 fw-bold'>
                {formatSeconds(metrics?.avg_talk_time_seconds ?? 0)}
              </div>
            </Col>
            <Col md={3}>
              <div className='text-muted small'>Calls / hour</div>
              <div className='fs-3 fw-bold'>{(metrics?.calls_per_hour ?? 0).toFixed(1)}</div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <h6 className='fw-semibold mb-2'>By agent</h6>
              <Table size='sm' responsive hover>
                <thead className='table-light'>
                  <tr>
                    <th>Agent</th>
                    <th className='text-end'>Calls</th>
                    <th className='text-end'>Dispo %</th>
                    <th className='text-end'>Avg talk</th>
                    <th className='text-end'>C/h</th>
                  </tr>
                </thead>
                <tbody>
                  {(metrics?.per_agent || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className='text-center text-muted py-3'>
                        No data
                      </td>
                    </tr>
                  )}
                  {(metrics?.per_agent || []).map((row) => (
                    <tr key={row.agent_user}>
                      <td>{row.agent_user}</td>
                      <td className='text-end'>{row.total_calls}</td>
                      <td className='text-end'>{formatPercent(row.dispo_rate)}</td>
                      <td className='text-end'>{formatSeconds(row.avg_talk_time_seconds)}</td>
                      <td className='text-end'>{row.calls_per_hour.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <h6 className='fw-semibold mb-2'>By campaign</h6>
              <Table size='sm' responsive hover>
                <thead className='table-light'>
                  <tr>
                    <th>Campaign</th>
                    <th className='text-end'>Calls</th>
                    <th className='text-end'>Dispo %</th>
                    <th className='text-end'>Avg talk</th>
                    <th className='text-end'>C/h</th>
                  </tr>
                </thead>
                <tbody>
                  {(metrics?.per_campaign || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className='text-center text-muted py-3'>
                        No data
                      </td>
                    </tr>
                  )}
                  {(metrics?.per_campaign || []).map((row) => (
                    <tr key={row.campaign_id}>
                      <td>{row.campaign_id}</td>
                      <td className='text-end'>{row.total_calls}</td>
                      <td className='text-end'>{formatPercent(row.dispo_rate)}</td>
                      <td className='text-end'>{formatSeconds(row.avg_talk_time_seconds)}</td>
                      <td className='text-end'>{row.calls_per_hour.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>

          {(metrics?.dispositions || []).length > 0 && (
            <>
              <h6 className='fw-semibold mb-2 mt-3'>Dispositions</h6>
              <div className='d-flex flex-wrap gap-2'>
                {metrics!.dispositions.map((row) => (
                  <Badge key={row.disposition} bg='light' text='dark' className='border'>
                    {row.disposition}: {row.count}
                  </Badge>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={Boolean(pendingSupervisorAction)}
        onHide={() => !runningSupervisorAction && setPendingSupervisorAction(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <i className='bi bi-exclamation-triangle-fill text-warning' />
            {pendingActionTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='fw-semibold mb-2'>
            {pendingActionAgents.length} agent{pendingActionAgents.length === 1 ? '' : 's'} selected
          </div>
          <div className='text-muted mb-3'>
            {pendingActionDescription}
          </div>
          <div className='d-flex flex-wrap gap-2 mb-3'>
            {pendingActionAgents.slice(0, 6).map((agentUser) => (
              <Badge key={agentUser} bg='light' text='dark' className='border'>
                {agentUser}
              </Badge>
            ))}
            {pendingActionAgents.length > 6 && (
              <Badge bg='light' text='dark' className='border'>
                +{pendingActionAgents.length - 6} more
              </Badge>
            )}
          </div>
          <div className='rounded-3 p-3' style={pendingActionNoticeStyle}>
            <div className='fw-semibold mb-1'>
              {pendingLiveRiskCount > 0
                ? `${pendingLiveRiskCount} selected agent${pendingLiveRiskCount === 1 ? '' : 's'} are in live call states`
                : 'No selected agents are currently in live call states'}
            </div>
            <div className='small' style={{ color: 'inherit' }}>
              {pendingActionType === 'logout'
                ? 'Use this only when the selected agents need to be removed immediately or their PolicyDial sessions are stuck.'
                : pendingActionType === 'pause'
                  ? 'Pause is useful when you need to temporarily stop multiple agents from receiving or placing calls.'
                  : 'Resume will attempt to return the selected agents to an active PolicyDial state.'}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            disabled={runningSupervisorAction}
            onClick={() => setPendingSupervisorAction(null)}
          >
            Cancel
          </Button>
          <Button
            variant={pendingActionPrimaryVariant}
            disabled={runningSupervisorAction}
            onClick={confirmSupervisorAction}
          >
            {pendingActionType === 'logout' ? (
              <i className='bi bi-box-arrow-right me-1' />
            ) : pendingActionType === 'pause' ? (
              <i className='bi bi-pause-circle me-1' />
            ) : (
              <i className='bi bi-play-circle me-1' />
            )}
            {runningSupervisorAction ? 'Working...' : pendingActionPrimaryLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
